import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';

const RoleBasedRoute = ({ 
  children, 
  requiredRoles = [], 
  checkSuperAdmin = false,
  requireAll = false, 
  fallbackPath = "/dashboard",
  showAccessDenied = true 
}) => {
  const { user, isAuthenticated, isInitialized, hasRole, hasAnyRole, hasAllRoles } = useContext(AuthContext);

  // Show loading while auth is initializing
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }



  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if(checkSuperAdmin){
    if(!user.is_staff || !user.is_superuser){
      return <Navigate to="/login" replace />;
    }
  }

  // If no roles required, just check authentication
  if (!requiredRoles || requiredRoles.length === 0) {
    return children;
  }

  // Check role permissions
  let hasPermission = false;
  
  if (typeof requiredRoles === 'string') {
    // Single role as string
    hasPermission = hasRole(requiredRoles);
  } else if (Array.isArray(requiredRoles)) {
    // Multiple roles as array
    if (requireAll) {
      // User must have ALL specified roles
      hasPermission = hasAllRoles(requiredRoles);
    } else {
      // User must have ANY of the specified roles (default)
      hasPermission = hasAnyRole(requiredRoles);
    }
  }

  // If user doesn't have required permissions
  if (!hasPermission) {
    if (showAccessDenied) {
      // Show access denied page
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.876c1.398 0 2.63-1.1 2.63-2.456 0-.357-.096-.703-.264-1.005L13.618 4.456c-.363-.624-1.011-1.007-1.618-1.007s-1.255.383-1.618 1.007L2.696 17.539c-.168.302-.264.648-.264 1.005C2.432 19.9 3.664 21 5.062 21z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès Refusé</h2>
            <p className="text-gray-600 mb-4">
              Vous n'avez pas les permissions nécessaires pour accéder à cette page.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Rôles requis: {Array.isArray(requiredRoles) ? requiredRoles.join(', ') : requiredRoles}
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
            >
              Retour
            </button>
          </div>
        </div>
      );
    } else {
      // Redirect to fallback path
      return <Navigate to={fallbackPath} replace />;
    }
  }

  // User has required permissions, render the component
  return children;
};

export default RoleBasedRoute;
